import axios from "axios";
import utils from "@utils";
import { mapValues, pickBy } from "@utils/lodash";
import { ShiftService, OfficerService, InvoiceService } from "@services";
import { apiErrorHandler } from "@redux-core/utils";
import { push } from "react-router-redux";
import Actions, { utils as adminUtils } from ".";

let cancel;

/* ------------- Thunks actions ------------- */
export const getShifts = () => (dispatch, getState) => {
	const { lang } = global;
	const {
		admin: {
			shifts: {
				list: { filters },
			},
		},
	} = getState();

	dispatch(Actions.updateLoaders("shifts", true));

	// Remove null filters before filtering the list
	const notNullFilters = pickBy(filters, item => item !== null);

	ShiftService.getAll(notNullFilters)
		.then(({ data }) => {
			dispatch(Actions.receiveShifts(data.ItemList, data.Count));
			dispatch(Actions.updateLoaders("shifts", false));
		})
		.catch(
			apiErrorHandler({
				dispatch,
				module: "admin",
				property: "loaders.shifts",
				defaultMessage: lang.t("Error getting shifts"),
			}),
		);
};

export const getShiftOfficers = id => (dispatch, getState) => {
	const { lang } = global;
	const {
		admin: {
			shifts: {
				list: { filters },
			},
		},
	} = getState();

	dispatch(Actions.receiveOfficers(id, [], true));

	// Remove null filters before filtering officers
	const notNullFilters = pickBy(filters, item => item !== null);

	ShiftService.getOfficers(id, notNullFilters)
		.then(({ data }) => {
			dispatch(Actions.receiveOfficers(id, data.Officers || [], false));
		})
		.catch(
			apiErrorHandler({
				dispatch,
				module: "admin",
				property: "loaders.shifts",
				defaultMessage: lang.t("Error getting Officers"),
			}),
		);
};

export const applyShiftFilters = filters => (dispatch, getState) => {
	const {
		admin: {
			shifts: {
				list: { filters: appliedFilters },
			},
		},
	} = getState();

	// Convert filters to integer except for officerName
	const parsedFilters = mapValues(filters, (item, key) =>
		item !== null && key !== "officerName" ? parseInt(item, 10) : item,
	);

	const allFilters = {
		...appliedFilters,
		...parsedFilters,
	};

	dispatch(Actions.updateShiftsFilters(allFilters));

	// Remove null filters before updating the URL
	const notNullFilters = pickBy(allFilters, item => item !== null);

	dispatch(
		push({
			pathname: "/admin/shift/list",
			query: notNullFilters,
		}),
	);

	dispatch(getShifts());
};

export const searchOfficers = value => (dispatch, getState) => {
	const { lang } = global;
	const {
		admin: {
			shifts: {
				form: {
					inputData: { Officers },
				},
			},
		},
	} = getState();

	// If there is a request already running, abort it and start a new one
	if (cancel) {
		cancel();
	}

	const queryParams = { vettedStatus: 2 };
	const data = { FirstName: value, LastName: value };
	const getCancelToken = c => {
		cancel = c;
	};

	dispatch(Actions.updateLoaders("searchOfficersList", true));

	OfficerService.search(queryParams, data, getCancelToken)
		.then(response => {
			cancel = undefined;

			// Check if the received officers are already assigned
			const assignedOfficersIds = Officers.value.map(item => item.Id);
			const officers = response.data.ItemList.map(item => ({
				...item,
				assigned: assignedOfficersIds.indexOf(item.Id) !== -1,
			}));

			dispatch(Actions.updateShiftForm("searchOfficersList", officers));
			dispatch(Actions.updateLoaders("searchOfficersList", false));
		})
		.catch(error => {
			if (!axios.isCancel(error)) {
				apiErrorHandler({
					dispatch,
					module: "admin",
					property: "loaders.searchOfficersList",
					defaultMessage: lang.t("Error getting Officers"),
				})(error);
			}
			dispatch(Actions.updateShiftForm("searchOfficersList", []));
		});
};

export const saveShift = (Id, formData) => dispatch => {
	const { lang } = global;
	const successCallback = () => {
		dispatch(adminUtils.updateShiftFormButton("success"));
		dispatch(getShifts());
		dispatch(Actions.toggleModal("shiftForm", false));
	};

	const errorCallback = error => {
		const { data } = error.response;
		const errorMessage = data ? undefined : data.ErrorMessage || data.Message;
		utils.notify({
			type: "error",
			message: errorMessage || lang.t("Something went wrong"),
			title: "Oops",
		});
		dispatch(adminUtils.updateShiftFormButton("error"));
	};

	if (Id) {
		ShiftService.edit({ ...formData, Id })
			.then(successCallback)
			.catch(errorCallback);
	} else {
		ShiftService.add(formData).then(successCallback).catch(errorCallback);
	}
};

export const loadShift = id => (dispatch, getState) => {
	const {
		admin: {
			shifts: {
				form: { inputData },
			},
		},
	} = getState();

	dispatch(Actions.updateLoaders("shift", true));

	ShiftService.getById(id)
		.then(({ data }) => {
			const formData = {
				...inputData,
				Name: { ...inputData.Name, value: data.Name },
				SupervisorId: { ...inputData.SupervisorId, value: data.SupervisorId },
				Officers: { value: data.Officers },
			};

			dispatch(
				Actions.receiveData([
					{ property: "shifts.form._defaultSupervisorName", value: data.SupervisorName },
					{ property: "shifts.form.id", value: data.Id },
					{ property: "shifts.form.inputData", value: formData },
					{ property: "shifts.form.title", value: "Edit Shift Form" },
					{ property: "loaders.shift", value: false },
				]),
			);
		})
		.catch(
			apiErrorHandler({
				dispatch,
				module: "admin",
				property: "loaders.shift",
				defaultMessage: "Error getting Shift",
			}),
		);
};

export const deleteShift = id => dispatch => {
	const { lang } = global;
	dispatch(Actions.updateLoaders("shifts", true));

	ShiftService.delete(id)
		.then(() => {
			utils.notify({
				type: "success",
				message: lang.t("Shift deleted successfully"),
			});

			dispatch(getShifts());
		})
		.catch(
			apiErrorHandler({
				dispatch,
				module: "admin",
				property: "loaders.shifts",
				defaultMessage: lang.t("Error deleting shift"),
			}),
		);
};

export const goToInvoice = id => dispatch => {
	const { lang } = global;
	if (Number.isNaN(parseInt(id, 10)) || id > 2147483647) {
		utils.notify({
			type: "error",
			message: lang.t("Invalid Invoice Id"),
		});

		return;
	}

	InvoiceService.getInvoiceById(id)
		.then(() => {
			dispatch(push(`/payments/${id}`));
		})
		.catch(() => {
			utils.notify({
				type: "error",
				message: lang.t("Invalid Invoice Id"),
			});
		});
};

export default {
	getShifts,
	getShiftOfficers,
	applyShiftFilters,
	searchOfficers,
	saveShift,
	loadShift,
	deleteShift,
	goToInvoice,
};
